// Spec layout <-> react-grid-layout. The spec stores the 12-column layout
// (app/models/dashboards/spec.py); phones get one column derived from it.

export const NEW_WIDGET_SIZE = { w: 6, h: 4 };

/** Spec layout for wide screens; one column, in reading order, on phones. */
export const buildLayouts = (spec) => {
  const placed = new Map((spec.layout || []).map((item) => [item.id, item]));
  const lg = spec.widgets.map((w) => {
    const item = placed.get(w.id);
    // A widget the spec forgot to place goes to the bottom; the grid compacts it.
    return item
      ? { i: w.id, x: item.x, y: item.y, w: item.w, h: item.h }
      : { i: w.id, x: 0, y: Infinity, ...NEW_WIDGET_SIZE };
  });
  const order = [...lg].sort((a, b) => a.y - b.y || a.x - b.x);
  let y = 0;
  const sm = order.map((item) => {
    const row = { i: item.i, x: 0, y, w: 1, h: Math.max(item.h, 2) };
    y += row.h;
    return row;
  });
  return { lg, sm };
};

/** The grid's wide layout back into spec layout items. */
export const toSpecLayout = (gridLayout) =>
  gridLayout.map(({ i, x, y, w, h }) => ({ id: i, x, y, w, h }));

/** Tiles packed into full rows, in reading order: a row takes the next tile,
 * then any later tile that still fits; spare columns are shared out across the
 * row and every tile in it gets the row's height, so no row has a gap. */
export const packRows = (layout) => {
  const left = [...layout].sort((a, b) => a.y - b.y || a.x - b.x);
  const placed = new Map();
  let y = 0;
  while (left.length) {
    const row = [left.shift()];
    let used = row[0].w;
    for (let i = 0; i < left.length && used < 12; ) {
      if (used + left[i].w <= 12) {
        used += left[i].w;
        row.push(...left.splice(i, 1));
      } else i += 1;
    }
    const h = Math.max(...row.map((item) => item.h));
    const extra = 12 - used;
    let x = 0;
    for (const [n, item] of row.entries()) {
      const w =
        item.w +
        Math.floor(extra / row.length) +
        (n < extra % row.length ? 1 : 0);
      placed.set(item.id, { ...item, x, y, w, h });
      x += w;
    }
    y += h;
  }
  return layout.map((item) => placed.get(item.id));
};

/** Spec layout with tile `id` set to `size` ({ w, h }), repacked so no row is
 * left with a gap; a tile with no layout item yet starts at the bottom. */
export const resizeTile = (layout, id, size) => {
  if (!size) return layout;
  const items = layout || [];
  if (!items.some((item) => item.id === id)) {
    const bottom = Math.max(0, ...items.map((item) => item.y + item.h));
    return packRows([...items, { id, x: 0, y: bottom, ...size }]);
  }
  return packRows(
    items.map((item) => (item.id === id ? { ...item, ...size } : item)),
  );
};
